import { freeShipmentSchema, type FreeShipmentInput } from "@erp/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Plus, Trash2, Truck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AddressFields } from "~/components/address-fields";
import { AsyncPicker } from "~/components/ui/async-picker";
import { Button } from "~/components/ui/button";
import { Field, FieldGroup } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import { Page, PageHeader, Panel } from "~/components/ui/page";
import {
  CustomerItem,
  searchCustomers,
  searchVariants,
  variantLabel,
  VariantItem,
  type CustomerOption,
  type VariantOption,
} from "~/features/sales/pickers";
import { emptyParcel, ParcelFields, parcelPayload, useLabelCarriers, type ParcelDraft } from "~/features/shipments/parcel-fields";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { addressDraft, emptyAddress, errorAt, schemaErrors, type AddressDraft, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { SerialPicker, useSerialTracking } from "~/features/stock/serial-input";

export const Route = createFileRoute("/_app/shipments/free")({ component: FreeShipment });

interface LineDraft {
  key: string;
  variant: VariantOption | null;
  quantity: string;
  serials: string[];
}
const newLine = (): LineDraft => ({ key: crypto.randomUUID(), variant: null, quantity: "1", serials: [] });

function BackLink() {
  const t = useT();
  return (
    <Link to="/shipments" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("shipments.title")}
    </Link>
  );
}

/** Stock leaving without a sales order: samples, loans, trade shows. Same BL, label and stock out as an order. */
function FreeShipment() {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const carriers = useLabelCarriers();
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [recipient, setRecipient] = useState({ name: "", email: "", phone: "" });
  const [address, setAddress] = useState<AddressDraft>(emptyAddress);
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const serialTracking = useSerialTracking();
  const [parcel, setParcel] = useState<ParcelDraft>(emptyParcel);
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});

  const pickCustomer = async (option: CustomerOption | null) => {
    setCustomer(option);
    if (!option) return;
    const detail = await unwrap(api.customers({ id: option.id }).get());
    setRecipient({ name: detail.displayName, email: detail.email ?? "", phone: detail.phone ?? "" });
    setAddress(addressDraft(detail.shippingAddress ?? detail.billingAddress));
  };

  const toPayload = (): FreeShipmentInput => ({
    ...parcelPayload(parcel, carriers),
    recipientName: recipient.name,
    email: recipient.email,
    phone: recipient.phone,
    address,
    note,
    lines: lines
      .filter((l) => l.variant)
      .map((l) => ({
        variantId: l.variant!.variantId,
        quantity: l.quantity,
        ...(serialTracking && l.variant!.serialTracked ? { serials: l.serials } : {}),
      })),
  });
  const errors = { ...serverErrors, ...(submitted ? schemaErrors(freeShipmentSchema, toPayload()) : {}) };

  const ship = useMutation({
    mutationFn: () => unwrap(api.shipments.free.post(freeShipmentSchema.parse(toPayload()))),
    onSuccess: (shipment) => {
      for (const key of ["shipments", "stock"]) void queryClient.invalidateQueries({ queryKey: [key] });
      toast.success(t("shipments.toasts.shipped", { number: shipment.number }));
      void navigate({ to: "/shipments/$id", params: { id: shipment.id } });
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = () => {
    setSubmitted(true);
    setServerErrors({});
    if (Object.keys(schemaErrors(freeShipmentSchema, toPayload())).length) return;
    ship.mutate();
  };
  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  return (
    <Page>
      <PageHeader back={<BackLink />} title={t("shipments.free.title")} meta={t("shipments.free.subtitle")} />
      <form
        className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_360px]"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex min-w-0 flex-col gap-5">
          <Panel className="p-5 md:p-6">
            <h2 className="mb-1 text-[15px] font-medium">{t("shipments.sections.lines")}</h2>
            <p className="mb-4 text-sm text-fg-2">{t("shipments.free.linesHint")}</p>
            <div className="flex flex-col gap-3">
              {lines.map((l, index) => (
                <div key={l.key} className="grid grid-cols-[minmax(0,1fr)_88px_auto] items-start gap-2">
                  <AsyncPicker
                    queryKey="variants"
                    search={searchVariants}
                    value={l.variant}
                    onChange={(variant) => updateLine(l.key, { variant })}
                    itemKey={(v) => v.variantId}
                    itemLabel={(v) => `${v.sku} — ${variantLabel(v)}`}
                    renderItem={(v) => <VariantItem variant={v} />}
                    placeholder={t("sav.pickVariant")}
                    aria-label={t("sav.fields.variant")}
                  />
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    aria-label={t("sav.fields.quantity")}
                    aria-invalid={Boolean(errors[`lines.${index}.quantity`])}
                    value={l.quantity}
                    onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                    className="text-right tabular"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.actions.remove")}
                    onClick={() => setLines(lines.length > 1 ? lines.filter((x) => x.key !== l.key) : [newLine()])}
                  >
                    <Trash2 />
                  </Button>
                  {l.variant ? (
                    <p className="col-span-3 -mt-1 text-xs text-fg-3 tabular">
                      {t("shipments.fields.onHand")} {format.number(l.variant.available)}
                    </p>
                  ) : null}
                  {l.variant && serialTracking && l.variant.serialTracked ? (
                    <div className="col-span-3">
                      <SerialPicker
                        variantId={l.variant.variantId}
                        value={l.serials}
                        onChange={(serials) => updateLine(l.key, { serials })}
                        expected={Number(l.quantity) || 0}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
              <Button variant="secondary" className="self-start" onClick={() => setLines([...lines, newLine()])}>
                <Plus />
                {t("sav.actions.addLine")}
              </Button>
              {submitted && errors.lines ? (
                <p role="alert" className="text-xs font-medium text-danger">
                  {t("errors.codes.shipment.empty")}
                </p>
              ) : null}
            </div>
          </Panel>

          <Panel className="p-5 md:p-6">
            <h2 className="mb-4 text-[15px] font-medium">{t("shipments.sections.recipient")}</h2>
            <FieldGroup>
              <Field label={t("sav.fields.customer")} optionalLabel={t("common.states.optional")}>
                <AsyncPicker
                  queryKey="customers"
                  search={searchCustomers}
                  value={customer}
                  onChange={(c) => void pickCustomer(c)}
                  itemKey={(c) => c.id}
                  itemLabel={(c) => c.displayName}
                  renderItem={(c) => <CustomerItem customer={c} />}
                  placeholder={t("sav.pickCustomer")}
                />
              </Field>
              <Field label={t("shipments.fields.recipient")} error={errorAt(t, errors, "recipientName")}>
                <Input value={recipient.name} onChange={(e) => setRecipient({ ...recipient, name: e.target.value })} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("common.fields.email")} optionalLabel={t("common.states.optional")}>
                  <Input
                    type="email"
                    value={recipient.email}
                    onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
                  />
                </Field>
                <Field label={t("common.fields.phone")} optionalLabel={t("common.states.optional")}>
                  <Input
                    type="tel"
                    value={recipient.phone}
                    onChange={(e) => setRecipient({ ...recipient, phone: e.target.value })}
                  />
                </Field>
              </div>
              <AddressFields value={address} onChange={setAddress} errors={errors} prefix="address" />
            </FieldGroup>
          </Panel>
        </div>

        <aside className="flex flex-col gap-5">
          <Panel className="flex flex-col gap-4 p-5 md:p-6">
            <h2 className="text-[15px] font-medium">{t("shipments.sections.parcel")}</h2>
            <ParcelFields value={parcel} onChange={setParcel} carriers={carriers} errors={errors} />
            <Field label={t("shipments.fields.note")} hint={t("shipments.free.noteHint")}>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </Panel>
          <div className="flex flex-col gap-2">
            <Button type="submit" variant="primary" size="lg" loading={ship.isPending}>
              <Truck />
              {t("shipments.actions.ship")}
            </Button>
            <p className="text-center text-xs text-fg-3">{t("shipments.free.stockLeaves")}</p>
          </div>
        </aside>
      </form>
    </Page>
  );
}
